/**
 * @quotech/auth — HR feature flag resolver
 *
 * PATH: packages/auth/src/useHrFeatures.ts
 * PURPOSE: Read the resolved HR module flag set for the current
 *          tenant from `/api/organisations/<org_id>/hr-features/`
 *          and cache it for the session, so every page-level gate
 *          and nav item in the merged HR hub at hr.quotech.ai can
 *          ask "should I render?" without hard-coding tenant names.
 *
 * Why this lives in `@quotech/auth`
 * =================================
 * The flags ARE the navigation. Apps like `apps/hr` and (during
 * the deprecation window) `apps/workforce` need them on every
 * route mount. Putting the hook in the shared auth package means
 * a single fetch/cache/contract — every consuming app gets the
 * same shape.
 *
 * Backend contract
 * ================
 * GET /api/organisations/<org_id>/hr-features/
 *
 * Returns (Phase 0/A + Phase 3 — see HR_UNIFICATION_PLAN.md):
 *   {
 *     module: bool, timeclock: bool, leave: bool, payroll: bool,
 *     documents: bool, compliance: bool, orgchart: bool,
 *     schedule: bool, timesheet: bool, allocation: bool,
 *     reconciliation: bool, approvals: bool,
 *     extra: {[k: string]: bool},
 *   }
 *
 * Auth: Bearer token + (X-Organisation-ID header). Any authenticated
 * org member can read; the response is intentionally not admin-only
 * because the flags drive what the user sees in the nav.
 *
 * Caching
 * =======
 * - Response cached in `localStorage` keyed on `org_id` for 1 hour.
 * - First page mount in a session refreshes; subsequent mounts read
 *   the cache. `refresh()` lets callers force a refetch (used after
 *   an admin toggles a flag in 0x3admin).
 * - When the BE endpoint isn't deployed yet (404) or unreachable,
 *   the hook falls back to `getDefaultHrFeatures()` (all-false) so
 *   apps render gracefully — no React crash, just no HR UI yet.
 */

import { useCallback, useEffect, useState } from 'react'

import { getAuthCookie, getOrgCookie } from './cookieAuth'

// =============================================================================
// TYPES
// =============================================================================

/**
 * Resolved HR feature flags for a tenant, keyed by the public flag
 * name the API uses. The `extra` map carries fine-grained sub-feature
 * toggles from `OrganisationSettings.hr_features` (free-form JSON)
 * so we can introduce more sub-features without a model migration.
 */
export interface HrFeatures {
  module: boolean
  timeclock: boolean
  leave: boolean
  payroll: boolean
  documents: boolean
  compliance: boolean
  orgchart: boolean
  schedule: boolean
  timesheet: boolean
  allocation: boolean
  reconciliation: boolean
  /**
   * Phase 3 — supervisor approval queue at hr.quotech.ai/inbox.
   * Independent of `timesheet` (worker-facing review) so tenants can
   * enable supervisor triage without exposing timesheets to workers.
   */
  approvals: boolean
  extra: Record<string, boolean>
}

// =============================================================================
// CONSTANTS
// =============================================================================

const API_BASE = `${
  import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai'
}/api`

const STORAGE_KEY_PREFIX = 'quotech:hr_features:'
const LAST_SYNC_PREFIX = 'quotech:hr_features_last_sync:'
const SYNC_INTERVAL_MS = 60 * 60 * 1000 // 1 hour

// =============================================================================
// PURE I→O HELPERS
// =============================================================================

/**
 * All-false defaults. Used when the BE endpoint isn't deployed yet,
 * the user isn't authenticated, or the network call fails. The
 * caller's nav/route gates render no HR UI in this state, which is
 * the safe degradation.
 */
export function getDefaultHrFeatures(): HrFeatures {
  return {
    module: false,
    timeclock: false,
    leave: false,
    payroll: false,
    documents: false,
    compliance: false,
    orgchart: false,
    schedule: false,
    timesheet: false,
    allocation: false,
    reconciliation: false,
    approvals: false,
    extra: {},
  }
}

/**
 * Coerce an API response (which has been through JSON parsing —
 * shape not statically guaranteed) into a strict HrFeatures.
 * Missing keys default to false; extra is passed through, with
 * non-bool values dropped.
 *
 * Pulled out so unit tests can verify the contract without spinning
 * up React.
 */
export function parseHrFeaturesResponse(raw: unknown): HrFeatures {
  const safe = (raw ?? {}) as Record<string, unknown>
  const flag = (key: string): boolean => safe[key] === true
  const extraRaw = safe['extra']
  const extra: Record<string, boolean> = {}
  if (extraRaw && typeof extraRaw === 'object') {
    for (const [k, v] of Object.entries(extraRaw as Record<string, unknown>)) {
      if (typeof v === 'boolean') extra[k] = v
    }
  }
  return {
    module: flag('module'),
    timeclock: flag('timeclock'),
    leave: flag('leave'),
    payroll: flag('payroll'),
    documents: flag('documents'),
    compliance: flag('compliance'),
    orgchart: flag('orgchart'),
    schedule: flag('schedule'),
    timesheet: flag('timesheet'),
    allocation: flag('allocation'),
    reconciliation: flag('reconciliation'),
    approvals: flag('approvals'),
    extra,
  }
}

// =============================================================================
// CACHE
// =============================================================================

function readCache(orgId: string): HrFeatures | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + orgId)
    if (!raw) return null
    return parseHrFeaturesResponse(JSON.parse(raw))
  } catch {
    return null
  }
}

function writeCache(orgId: string, features: HrFeatures): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + orgId, JSON.stringify(features))
    localStorage.setItem(LAST_SYNC_PREFIX + orgId, String(Date.now()))
  } catch {
    // localStorage full / disabled → silently degrade. Hook stays
    // useable; we just refetch every render-cycle instead of
    // deduping. Better than crashing.
  }
}

function shouldRefetch(orgId: string): boolean {
  if (typeof localStorage === 'undefined') return true
  const last = localStorage.getItem(LAST_SYNC_PREFIX + orgId)
  if (!last) return true
  return Date.now() - parseInt(last, 10) >= SYNC_INTERVAL_MS
}

// =============================================================================
// API
// =============================================================================

/**
 * Fetch the HR features payload for an org. Returns null on hard
 * failures (no auth, network error, 5xx). Returns
 * `getDefaultHrFeatures()` on 404 / 403 — for the deploy window
 * where the endpoint may not be live yet, or where a non-member is
 * making the call.
 *
 * Exported for tests + for callers who want to refetch without the
 * React hook (e.g. after a user switches active org).
 */
export async function fetchHrFeatures(
  orgId: string,
  token?: string,
): Promise<HrFeatures | null> {
  const accessToken = token || getAuthCookie()
  if (!accessToken) return null
  if (!orgId) return null

  try {
    const response = await fetch(
      `${API_BASE}/organisations/${encodeURIComponent(orgId)}/hr-features/`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Organisation-ID': orgId,
        },
      },
    )

    if (response.status === 404 || response.status === 403) {
      // Endpoint not deployed yet OR user isn't a member of this
      // org. Either way: the safe FE response is "no HR features".
      return getDefaultHrFeatures()
    }
    if (!response.ok) {
      console.warn(
        '[@quotech/auth] hr-features fetch failed:',
        response.status,
      )
      return null
    }

    const data = await response.json()
    return parseHrFeaturesResponse(data)
  } catch (err) {
    console.warn('[@quotech/auth] hr-features fetch error:', err)
    return null
  }
}

// =============================================================================
// REACT HOOK
// =============================================================================

export interface UseHrFeaturesReturn {
  features: HrFeatures
  isLoading: boolean
  /** Force a refetch from the backend. */
  refresh: () => Promise<void>
}

/**
 * React hook — returns the resolved HR feature set for the current
 * org plus a `refresh()` callback. Strategy:
 *
 *   1. On mount, read the localStorage cache → render immediately
 *      (fast first paint, no flash).
 *   2. If the cache is stale or missing, fire a backend refetch
 *      → second render with the fresh data.
 *   3. `refresh()` lets the caller force a refetch (used by 0x3admin
 *      after a flag toggle).
 *
 * If there's no auth or no org, returns all-false defaults — every
 * gate stays closed, no UI flicker.
 */
export function useHrFeatures(): UseHrFeaturesReturn {
  const [features, setFeatures] = useState<HrFeatures>(() => {
    const orgId = getOrgCookie()?.id
    if (!orgId) return getDefaultHrFeatures()
    return readCache(orgId) ?? getDefaultHrFeatures()
  })
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    const orgId = getOrgCookie()?.id
    if (!orgId) return
    setIsLoading(true)
    try {
      const fresh = await fetchHrFeatures(orgId)
      if (fresh) {
        setFeatures(fresh)
        writeCache(orgId, fresh)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const orgId = getOrgCookie()?.id
    if (!orgId) return
    if (shouldRefetch(orgId)) {
      void refresh()
    }
  }, [refresh])

  return { features, isLoading, refresh }
}
